/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { useEffect, useMemo, useRef, useState } from 'react';

// Updates the height of a <textarea> when the value changes.
export const useTypewriter = (props: {
  initialStr?: string,
  appendStr: string | null,
  onWrite?: (output: string) => void,
  onEditorAIWrite?: (result: string) => void,
}) => {
  const { initialStr = '', appendStr, onWrite, onEditorAIWrite } = props;

  const [result, setResult] = useState<string>('');
  const [isWriting, setIsWriting] = useState(false);
  const [isComplete, setIsComplete] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout>();

  const tokens = useMemo(() => {
    setResult('');
    return appendStr ? appendStr.split('') : []
  }, [appendStr]);

  const appendResult = (token: string | undefined) => setResult(r => `${r}${token ?? ''}`);

  useEffect(() => {
    if (tokens.length === 0) {
      return;
    }
    setIsComplete(false);
    setIsWriting(true);
    appendResult(tokens.shift());
    intervalRef.current = setInterval(() => {
      appendResult(tokens.shift());
      appendResult(tokens.shift());
      appendResult(tokens.shift());
      appendResult(tokens.shift());
      if (tokens.length === 0) {
        setIsComplete(true);
        clearInterval(intervalRef.current);
      }
    }, 20);

    return function clear() {
      clearInterval(intervalRef.current);
    }
  }, [tokens]);

  useEffect(() => {
    if (!isComplete) {
      onEditorAIWrite?.(result);
      return;
    }
    if (isWriting) {
      setIsWriting(false);
      onWrite?.(`${initialStr}${result ? result : ''}`);
    }
  }, [initialStr, isWriting, isComplete, onWrite, onEditorAIWrite, result]);

  return {
    liveResult: isWriting ? result : null,
    finalResult: isWriting ? null : result,
    isWriting
  }
};
